import { Fragment, useEffect, useState, useCallback } from 'react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'

interface LinhaSolicitacao {
  est: number
  numero: number
  lin: number
  datsol: string
  hora: string
  usuario: string
  ccusto: string
  urg: string
  dtlib: string | null
  item: number
  descricao: string
  qtsol: string
  qtlib: string
  prazo: string
  dest: string
  qtatend: string
  status: string
  statusLabel: string
  ordemcompra: number | null
  motivocancelamento: string | null
}

interface Filtros {
  empcodDe: string
  empcodAte: string
  dataDe: string
  dataAte: string
  centroCustoCod: string
  numero: string
  item: string
  destinoCod: string
  destinoDes: string
  usuario: string
  situacao: string
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }

function filtroPadrao(): Filtros {
  return {
    empcodDe: '1', empcodAte: '999', dataDe: hojeISO(), dataAte: hojeISO(),
    centroCustoCod: '', numero: '', item: '',
    destinoCod: '', destinoDes: '', usuario: '', situacao: '',
  }
}

function fmtData(iso: string | null): string {
  if (!iso || iso.slice(0, 4) <= '0001') return '-'
  return iso.slice(0, 10).split('-').reverse().join('/')
}
function fmtQtd(v: string): string {
  return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 })
}

const STATUS_COR: Record<string, { cor: string; fundo: string }> = {
  Pendente: { cor: '#991b1b', fundo: '#fee2e2' },
  Cancelada: { cor: '#6b7280', fundo: '#f3f4f6' },
  Atendida: { cor: '#15803d', fundo: '#dcfce7' },
  'A.Parcial': { cor: '#92400e', fundo: '#fef3c7' },
  Bloqueada: { cor: '#7c2d12', fundo: '#ffedd5' },
  Cotada: { cor: '#1d4ed8', fundo: '#dbeafe' },
  'Em Ordem de Compra': { cor: '#6d28d9', fundo: '#ede9fe' },
  'Ordem Parcial': { cor: '#7c3aed', fundo: '#f3e8ff' },
}

function Grupo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{
        fontSize: '0.66rem', fontWeight: 700, color: '#1a6ea8',
        textTransform: 'uppercase', letterSpacing: '0.05em',
      }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
        {children}
      </div>
    </div>
  )
}

export default function ConsultaSolicitacaoCompra() {
  const [lista, setLista] = useState<LinhaSolicitacao[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [filtros, setFiltros] = useState<Filtros>(filtroPadrao)
  const [pesquisaAtiva, setPesquisaAtiva] = useState<Filtros>(filtroPadrao)

function paramsDeFiltro(f: Filtros): Record<string, string> {
    const params: Record<string, string> = {}
    if (f.empcodDe) params.empcodDe = f.empcodDe
    if (f.empcodAte) params.empcodAte = f.empcodAte
    if (!f.numero) {
      if (f.dataDe) params.dataDe = f.dataDe
      if (f.dataAte) params.dataAte = f.dataAte
    }
    if (f.centroCustoCod) params.centroCustoCod = f.centroCustoCod
    if (f.numero) params.numero = f.numero
    if (f.item) params.item = f.item
    if (f.destinoCod) params.destinoCod = f.destinoCod
    if (f.usuario) params.usuario = f.usuario
    if (f.situacao) params.situacao = f.situacao
    return params
  }

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(null)
    try {
      const r = await api.get('/erp/solicitacao-compra/lista', { params: paramsDeFiltro(pesquisaAtiva) })
      setLista(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao carregar lista')
    } finally {
      setCarregando(false)
    }
  }, [pesquisaAtiva])

  useEffect(() => { carregar() }, [carregar])

  function pesquisar() { setPesquisaAtiva({ ...filtros }) }

  function abrirPdfListagem() {
    const qs = new URLSearchParams(paramsDeFiltro(pesquisaAtiva)).toString()
    window.open(`/api/erp/solicitacao-compra/lista/pdf${qs ? '?' + qs : ''}`, '_blank')
  }

  const [resumo, setResumo] = useState<{ est: number; numero: number } | null>(null)
  const [resumoDados, setResumoDados] = useState<any>(null)
  const [resumoCarregando, setResumoCarregando] = useState(false)

  const [selecionados, setSelecionados] = useState<Record<string, LinhaSolicitacao>>({})
  const [itensCancelar, setItensCancelar] = useState<LinhaSolicitacao[] | null>(null)
  const [motivoCancel, setMotivoCancel] = useState('')
  const [erroCancel, setErroCancel] = useState<string | null>(null)
  const [cancelando, setCancelando] = useState(false)

  async function abrirResumo(est: number, numero: number) {
    setResumo({ est, numero }); setResumoDados(null); setResumoCarregando(true)
    try {
      const r = await api.get(`/erp/solicitacao-compra/${est}/${numero}`)
      setResumoDados(r.data)
    } catch {
      setResumoDados(null)
    } finally {
      setResumoCarregando(false)
    }
  }
  function fecharResumo() { setResumo(null); setResumoDados(null) }
  function limpar() { const p = filtroPadrao(); setFiltros(p); setPesquisaAtiva(p) }

  function chaveItem(row: LinhaSolicitacao) { return `${row.est}-${row.numero}-${row.lin}` }

  function toggleSelecao(row: LinhaSolicitacao) {
    if (row.statusLabel !== 'Pendente') return
    setSelecionados(s => {
      const k = chaveItem(row)
      const next = { ...s }
      if (next[k]) delete next[k]; else next[k] = row
      return next
    })
  }
  function selecionarTodosDoGrupo(row: LinhaSolicitacao) {
    const doGrupo = lista.filter(r => r.est === row.est && r.numero === row.numero && r.statusLabel === 'Pendente')
    setSelecionados(s => {
      const next = { ...s }
      doGrupo.forEach(r => { next[chaveItem(r)] = r })
      return next
    })
  }
  function limparSelecao() { setSelecionados({}) }

  function abrirCancelar(itens: LinhaSolicitacao[]) {
    setItensCancelar(itens); setMotivoCancel(''); setErroCancel(null)
  }
  function fecharCancelar() {
    if (cancelando) return
    setItensCancelar(null); setMotivoCancel(''); setErroCancel(null)
  }
  async function confirmarCancelamento() {
    if (!itensCancelar || itensCancelar.length === 0) return
    if (motivoCancel.trim().length < 5) {
      setErroCancel('Motivo deve ter no mínimo 5 caracteres')
      return
    }
    setCancelando(true); setErroCancel(null)
    try {
      await api.post('/erp/solicitacao-compra/cancelar-itens', {
        itens: itensCancelar.map(r => ({ empcod: r.est, numero: r.numero, linha: r.lin })),
        motivo: motivoCancel.trim(),
      })
      setItensCancelar(null)
      setSelecionados({})
      await carregar()
    } catch (e: any) {
      setErroCancel(e?.response?.data?.error || 'Erro ao cancelar os itens')
    } finally {
      setCancelando(false)
    }
  }

  const inp: React.CSSProperties = {
    border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
    fontSize: '0.82rem', height: 30, background: '#fff',
  }
  const th: React.CSSProperties = {
    padding: '9px 10px', textAlign: 'left', color: '#fff', fontWeight: 700,
    fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
  }
  const td: React.CSSProperties = { padding: '8px 10px', color: '#374151' }

  let ultimoNumeroGrupo: number | null = null
  let grupoPar = false

  return (
    <div className="card">
      <h2 style={{
        fontSize: '1.1rem', fontWeight: 700, margin: '0 0 1rem', color: '#1a6ea8',
        paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff',
      }}>
        Consultar Solicitações de Compra
      </h2>

      <div style={{
        background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
        borderRadius: '0.5rem', padding: '0.7rem 0.9rem', marginBottom: '1rem',
      }}>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.6rem 1rem', alignItems: 'flex-end', maxWidth: 920,
      }}>
        <Grupo label="Estabelecimento">
          <input style={{ ...inp, width: 55 }} type="number" value={filtros.empcodDe}
            onChange={e => setFiltros(f => ({ ...f, empcodDe: e.target.value }))} />
          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>a</span>
          <input style={{ ...inp, width: 55 }} type="number" value={filtros.empcodAte}
            onChange={e => setFiltros(f => ({ ...f, empcodAte: e.target.value }))} />
        </Grupo>
        <Grupo label="Data Solicitação">
          <input style={{ ...inp, width: 130 }} type="date" value={filtros.dataDe}
            onChange={e => setFiltros(f => ({ ...f, dataDe: e.target.value }))} />
          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>a</span>
          <input style={{ ...inp, width: 130 }} type="date" value={filtros.dataAte}
            onChange={e => setFiltros(f => ({ ...f, dataAte: e.target.value }))} />
        </Grupo>
        <Grupo label="Solicitação">
          <input style={{ ...inp, width: 90 }} type="number" value={filtros.numero}
            onChange={e => setFiltros(f => ({ ...f, numero: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        </Grupo>
        <Grupo label="Item">
          <input style={{ ...inp, width: 80 }} type="number" value={filtros.item}
            onChange={e => setFiltros(f => ({ ...f, item: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        </Grupo>
        <Grupo label="C. Custo">
          <div style={{ width: 100 }}>
            <LookupSelect
              endpoint="/erp/ccucad"
              codKey="ccu_cod" descKey="ccu_des"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1"><span className="nat-item-cod">{item.ccu_cod}</span></div>
                  <div className="nat-item-dsc">{item.ccu_des}</div>
                </>
              )}
              codValue={filtros.centroCustoCod} descValue={filtros.centroCustoCod}
              onSelect={cod => setFiltros(f => ({ ...f, centroCustoCod: cod }))}
              placeholder="Todos..." listWidth={260}
            />
          </div>
        </Grupo>
        <Grupo label="Destino">
          <div style={{ width: 100 }}>
            <LookupSelect
              endpoint="/erp/oedcad/lista"
              codKey="oed_cod" descKey="oed_des"
              renderItem={(item: any) => (
                <>
                  <div className="nat-item-linha1"><span className="nat-item-cod">{item.oed_cod}</span></div>
                  <div className="nat-item-dsc">{item.oed_des}</div>
                </>
              )}
              codValue={filtros.destinoCod} descValue={filtros.destinoCod}
              onSelect={(cod, des) => setFiltros(f => ({ ...f, destinoCod: cod, destinoDes: des }))}
              placeholder="Todos..." listWidth={260}
            />
          </div>
        </Grupo>
        <Grupo label="Usuário">
          <input style={{ ...inp, width: 110 }} value={filtros.usuario}
            onChange={e => setFiltros(f => ({ ...f, usuario: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && pesquisar()} />
        </Grupo>
        <Grupo label="Situação">
          <select style={{ ...inp, width: 150 }} value={filtros.situacao}
            onChange={e => setFiltros(f => ({ ...f, situacao: e.target.value }))}>
            <option value="">Geral</option>
            <option value="A">Pendentes</option>
            <option value="C">Canceladas</option>
            <option value="D">Atendidas</option>
            <option value="P">Atendidas Parcial</option>
            <option value="X">Bloqueadas</option>
          </select>
        </Grupo>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem' }}>
        <button className="btn btn-primary btn-sm" onClick={pesquisar} disabled={carregando}
          style={{ height: 30 }}>
          {carregando ? 'Buscando...' : 'Pesquisar'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={limpar}
          style={{ height: 30 }}>
          Limpar
        </button>
        <button className="btn btn-sm" onClick={abrirPdfListagem}
          disabled={lista.length === 0}
          style={{ height: 30, background: '#dc2626', color: '#fff' }}>
          Gerar PDF
        </button>
      </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {Object.keys(selecionados).length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.75rem',
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '0.5rem 0.8rem',
        }}>
          <span style={{ fontSize: '0.82rem', color: '#991b1b' }}>
            {Object.keys(selecionados).length} item(ns) selecionado(s)
          </span>
          <button onClick={() => abrirCancelar(Object.values(selecionados))}
            style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
            Cancelar Selecionados
          </button>
          <button onClick={limparSelecao}
            style={{ background: 'none', border: '1px solid #d1d5db', color: '#6b7280', borderRadius: 4, padding: '4px 12px', fontSize: '0.78rem', cursor: 'pointer' }}>
            Limpar seleção
          </button>
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <table style={{ fontSize: '0.8rem', minWidth: 1300, borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr style={{ background: '#1a6ea8' }}>
              <th style={{ ...th, textAlign: 'center', width: 44 }}></th>
              <th style={{ ...th, textAlign: 'center', width: 40 }}>Est</th>
              <th style={{ ...th, width: 65 }}>Número</th>
              <th style={{ ...th, textAlign: 'center', width: 35 }}>Lin</th>
              <th style={{ ...th, width: 80 }}>Dt.Sol.</th>
              <th style={{ ...th, width: 55 }}>Hora</th>
              <th style={{ ...th, width: 90 }}>Usuário</th>
              <th style={{ ...th, textAlign: 'center', width: 55 }}>C.Custo</th>
              <th style={{ ...th, textAlign: 'center', width: 35 }}>Urg</th>
              <th style={{ ...th, width: 80 }}>Dt.Lib.</th>
              <th style={{ ...th, width: 60 }}>Item</th>
              <th style={th}>Descrição</th>
              <th style={{ ...th, textAlign: 'right', width: 80 }}>Qt.Sol.</th>
              <th style={{ ...th, width: 80 }}>Prazo</th>
              <th style={{ ...th, textAlign: 'center', width: 50 }}>Dest</th>
              <th style={{ ...th, textAlign: 'right', width: 80 }}>Qt.Atend.</th>
              <th style={{ ...th, width: 130 }}>Status</th>
              <th style={{ ...th, width: 70 }}>O.Compr</th>
              <th style={{ ...th, textAlign: 'center', width: 80 }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {carregando && (
              <tr><td colSpan={19} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Carregando...</td></tr>
            )}
            {!carregando && lista.length === 0 && (
              <tr><td colSpan={19} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma solicitação encontrada</td></tr>
            )}
            {lista.map(row => {
              const cor = STATUS_COR[row.statusLabel] ?? { cor: '#374151', fundo: '#f3f4f6' }
              const novoGrupo = row.numero !== ultimoNumeroGrupo
              if (novoGrupo) { grupoPar = !grupoPar; ultimoNumeroGrupo = row.numero }
              const pendentesDoGrupo = novoGrupo
                ? lista.filter(r => r.est === row.est && r.numero === row.numero && r.statusLabel === 'Pendente').length
                : 0
              return (
                <tr key={`${row.numero}-${row.lin}`} style={{
                  background: grupoPar ? '#e8eefa' : '#fff',
                  borderTop: novoGrupo ? '2px solid #b9c6e0' : undefined,
                  borderBottom: '1px solid #d5dceb',
                }}>
                  <td style={{ ...td, textAlign: 'center', padding: '6px 4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      {row.statusLabel === 'Pendente' ? (
                        <input type="checkbox" checked={!!selecionados[chaveItem(row)]} onChange={() => toggleSelecao(row)}
                          style={{ width: 14, height: 14, margin: 0, cursor: 'pointer' }} />
                      ) : (
                        <div style={{ width: 14, height: 14 }} />
                      )}
                      {novoGrupo && pendentesDoGrupo > 1 && (
                        <button onClick={() => selecionarTodosDoGrupo(row)} title="Selecionar todos os itens Pendentes desta solicitação"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1d4ed8', fontSize: '0.95rem', lineHeight: 1 }}>
                          ☑
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>{row.est}</td>
                  <td style={{ ...td, fontWeight: 700 }}>
                    <span onClick={() => abrirResumo(row.est, row.numero)}
                      style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                      title="Clique para ver o resumo da solicitação">
                      {row.numero}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>{row.lin}</td>
                  <td style={td}>{fmtData(row.datsol)}</td>
                  <td style={td}>{row.hora}</td>
                  <td style={td}>{row.usuario}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{row.ccusto || '-'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{row.urg}</td>
                  <td style={td}>{fmtData(row.dtlib)}</td>
                  <td style={td}>{row.item}</td>
                  <td style={td}>{row.descricao}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtQtd(row.qtsol)}</td>
                  <td style={td}>{fmtData(row.prazo)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{row.dest}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmtQtd(row.qtatend)}</td>
                  <td style={td}>
                    <span title={row.motivocancelamento || ''} style={{
                      fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: cor.fundo, color: cor.cor,
                    }}>
                      {row.statusLabel}
                    </span>
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{row.ordemcompra || '-'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {row.statusLabel === 'Pendente' && (
                      <button onClick={() => abrirCancelar([row])}
                        style={{
                          background: 'none', border: '1px solid #dc2626', color: '#dc2626',
                          borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem',
                          fontWeight: 600, cursor: 'pointer',
                        }}>
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {resumo && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          paddingTop: '4rem', zIndex: 1000,
        }} onClick={fecharResumo}>
          <div style={{ background: '#fff', borderRadius: 8, width: 640, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ background: '#1a6ea8', color: '#fff', padding: '0.9rem 1.25rem', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700 }}>Solicitação nº {resumo.numero}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Estabelecimento {resumo.est}</div>
              </div>
              <button onClick={fecharResumo} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', borderRadius: 4, padding: '4px 10px', fontSize: '0.9rem' }}>✕</button>
            </div>

            {resumoCarregando && <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Carregando...</div>}

            {!resumoCarregando && !resumoDados && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#b91c1c' }}>Não foi possível carregar os dados.</div>
            )}

            {!resumoCarregando && resumoDados && (
              <div style={{ padding: '1.1rem 1.25rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1.5rem', fontSize: '0.82rem', marginBottom: '0.9rem' }}>
                  <div><b>Data:</b> {fmtData(resumoDados.dataSolicitacao)}</div>
                  <div><b>Hora:</b> {resumoDados.horaSolicitacao}</div>
                  <div><b>Solicitante:</b> {resumoDados.nomeSolicitante || '-'}</div>
                  <div><b>Urgente:</b> {resumoDados.urgente === 'S' ? 'Sim' : 'Não'}</div>
                  <div><b>Centro de Custo:</b> {resumoDados.centroCustoCod || '-'}</div>
                  <div><b>Conta Financeira:</b> {resumoDados.contaFinanceiraCod || '-'}</div>
                  <div><b>Conta Administrativa:</b> {resumoDados.ctaAdministrativaCod || '-'}</div>
                  <div><b>Situação:</b> {resumoDados.situacao === 'CA' ? 'Cancelada' : resumoDados.situacao === '00' ? 'Pendente' : resumoDados.situacao}</div>
                </div>
                {resumoDados.situacao === 'CA' && resumoDados.motivoCancelamento && (
                  <div style={{ fontSize: '0.82rem', marginBottom: '0.9rem', color: '#dc2626' }}>
                    <b>Motivo do Cancelamento:</b> {resumoDados.motivoCancelamento}
                  </div>
                )}
                {resumoDados.observacoes && (
                  <div style={{ fontSize: '0.82rem', marginBottom: '0.9rem' }}>
                    <b>Observações:</b> {resumoDados.observacoes}
                  </div>
                )}

                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.4rem' }}>
                  Itens ({resumoDados.itens?.length ?? 0})
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Item</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Descrição</th>
                      <th style={{ textAlign: 'right', padding: '4px 6px' }}>Qtd.</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>Un.</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>Entrega</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>Destino</th>
                      <th style={{ textAlign: 'center', padding: '4px 6px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resumoDados.itens?.map((it: any) => (
                      <Fragment key={it.linha}>
                        <tr style={{ borderBottom: it.motivoCancelamento ? 'none' : '1px solid #f3f4f6' }}>
                          <td style={{ padding: '4px 6px' }}>{it.itemCod}</td>
                          <td style={{ padding: '4px 6px' }}>{it.itemDes}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>{fmtQtd(it.quantidade)}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>{it.unidade}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>{fmtData(it.dataEntrega)}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center' }}>{it.destCompraCod}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'center', color: it.statusLabel === 'Cancelada' ? '#dc2626' : undefined }}
                            title={it.motivoCancelamento || ''}>
                            {it.statusLabel}
                          </td>
                        </tr>
                        {it.motivoCancelamento && (
                          <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td colSpan={7} style={{ padding: '0 6px 6px', color: '#dc2626', fontSize: '0.74rem' }}>
                              Motivo: {it.motivoCancelamento}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>

                <div style={{ display: 'flex', gap: 8, marginTop: '1.1rem' }}>
                  <button className="btn btn-primary btn-sm"
                    onClick={() => window.open(`/api/erp/solicitacao-compra/${resumo.est}/${resumo.numero}/comprovante`, '_blank')}>
                    Imprimir Comprovante
                  </button>
                  <button className="btn-editar" onClick={fecharResumo}>
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {itensCancelar && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={fecharCancelar}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.5rem',
            width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>
              {itensCancelar.length === 1 ? 'Cancelar Item da Solicitação' : `Cancelar ${itensCancelar.length} Itens`}
            </h3>
            <ul style={{ margin: '0 0 1rem', paddingLeft: '1.1rem', fontSize: '0.82rem', color: '#6b7280', maxHeight: 120, overflowY: 'auto' }}>
              {itensCancelar.map(it => (
                <li key={chaveItem(it)}>Solicitação nº {it.numero} — Item {it.item} ({it.descricao})</li>
              ))}
            </ul>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Motivo do cancelamento
            </label>
            <textarea
              autoFocus
              value={motivoCancel}
              onChange={e => { setMotivoCancel(e.target.value); setErroCancel(null) }}
              rows={4}
              maxLength={300}
              placeholder="Informe o motivo do cancelamento..."
              style={{
                width: '100%', boxSizing: 'border-box',
                border: `1px solid ${erroCancel ? '#fca5a5' : '#d1d5db'}`,
                borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem', resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontSize: '0.75rem', color: erroCancel ? '#dc2626' : '#9ca3af' }}>
                {erroCancel ?? `${motivoCancel.length}/300`}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-sm" onClick={fecharCancelar} disabled={cancelando}
                style={{ color: '#6b7280' }}>
                Voltar
              </button>
              <button
                onClick={confirmarCancelamento}
                disabled={cancelando || motivoCancel.trim().length < 5}
                style={{
                  background: '#dc2626', color: '#fff', border: 'none',
                  borderRadius: 4, padding: '5px 16px', fontSize: '0.82rem',
                  fontWeight: 600, cursor: cancelando ? 'not-allowed' : 'pointer',
                  opacity: cancelando || motivoCancel.trim().length < 5 ? 0.6 : 1,
                }}>
                {cancelando ? 'Cancelando...' : 'Confirmar Cancelamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
